import React, { useEffect, useRef, useState } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import './index.scss'
import {
  Icon,
  Offscreen,
  Toast,
  Tooltip,
  TooltipTabstop,
} from '@deque/cauldron-react'
import isBrowser from '../../../lib/is-browser'
import { useGatsbyPageContext } from '../../../context/GatsbyPageContext'
import FreeTrialLink from '../FreeTrialLink'
import { useTranslation } from 'react-i18next'
import { useLocation } from '@reach/router'

interface HeaderProps {
  title: string
  productVersion: string
  mainRef: React.RefObject<HTMLDivElement>
  showVersionModal: () => void
  subtitle?: string
}

export default function ArticleHeader(props: HeaderProps) {
  const { title, productVersion, mainRef, showVersionModal, subtitle } = props
  const { freeTrialUrl, hasPiiTip } = useGatsbyPageContext()
  const { t } = useTranslation()
  const location = useLocation()

  const [showCopyToast, setShowCopyToast] = useState(false)
  const [showRedirectedToast, setShowRedirectedToast] = useState(false)
  const copyButtonRef = useRef<HTMLButtonElement>(null)
  const versionButtonRef = useRef<HTMLButtonElement>(null)
  const clipboardUrl = isBrowser() ? location.origin + location.pathname : ''

  const redirectedFrom = location.search.split('redirectedFrom=')[1]

  useEffect(() => {
    // Show the redirected toast if the user was redirected from a different language
    setShowRedirectedToast(!!redirectedFrom)
  }, [redirectedFrom])

  return (
    <div className="Article__Header">
      <div className="Title">
        <div className="Title__Heading">
          <h1>{title}</h1>
        </div>
        <Toast
          type="info"
          show={showRedirectedToast}
          onDismiss={() => {
            setShowRedirectedToast(false)
            mainRef.current?.focus()
          }}
        >
          {/* Show toast message in the language that was originally requested */}
          <span>{t('unavailableInLanguage', { lng: redirectedFrom })}</span>
        </Toast>
        <Toast
          type="confirmation"
          show={showCopyToast}
          onDismiss={() => {
            setShowCopyToast(false)
            mainRef.current?.focus()
          }}
        >
          <span>{t('linkCopied')}</span>
        </Toast>
        <CopyToClipboard
          text={clipboardUrl}
          onCopy={() => setShowCopyToast(true)}
        >
          <button className="button" ref={copyButtonRef}>
            <img
              src="/images/light/icons/copy.svg"
              alt={t('copyPageLocation')}
            />
          </button>
        </CopyToClipboard>
        <Tooltip
          target={copyButtonRef as React.RefObject<HTMLButtonElement>}
          placement="top"
        >
          <span>{t('copyPageLocation')}</span>
        </Tooltip>
      </div>
      {subtitle && (
        <div className="Subtitle">
          <p>{subtitle}</p>
        </div>
      )}
      {freeTrialUrl && (
        <FreeTrialLink
          className="FreeTrial"
          url={freeTrialUrl}
          variant="button-secondary"
        />
      )}
      <div className="VersionSelect">
        <button
          ref={versionButtonRef}
          onClick={(e) => {
            e.preventDefault()
            showVersionModal()
          }}
        >
          <Offscreen>{t('changeProductVersion')}</Offscreen>
          <span>v</span>
          <span>{productVersion}</span>
          <Icon type="triangle-down" />
        </button>
        <Tooltip
          target={versionButtonRef as React.RefObject<HTMLButtonElement>}
          placement="right"
        >
          <span>{t('changeProductVersion')}</span>
        </Tooltip>
      </div>
      {hasPiiTip && (
        <div className="PiiTip">
          <span>{t('notForPersonalData')}</span>
          <TooltipTabstop
            tooltip={t('notForPersonalDataTooltip')}
            variant="info"
            placement="right"
          >
            <Icon type="info-circle-alt" label={t('notForPersonalData')} />
          </TooltipTabstop>
        </div>
      )}
    </div>
  )
}
